import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AtSign, Lock, LogIn, User, UserPlus } from 'lucide-react'
import { MIN_PASSWORD_LENGTH } from '@shared/constants'
import AuthLayout, { AuthNotice, fieldErrorsFrom } from '@/components/auth/AuthLayout'
import PasswordField, { passwordStrength } from '@/components/auth/PasswordField'
import type { AuthRedirectState } from '@/components/layout/RequireAuth'
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input
} from '@/components/ui'
import { isApiError } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'

type Mode = 'signin' | 'register'

/**
 * Mirrors the router's own guard rather than importing it: this component also
 * runs the redirect itself, and both copies must reject the same paths.
 */
function returnPath(state: unknown): string {
  const from = (state as AuthRedirectState | null)?.from
  if (typeof from !== 'string' || !from.startsWith('/')) return '/sites'
  // `//host` is a protocol-relative URL, and bouncing back here would loop.
  const unsafe = ['//', '/login', '/setup'].some((prefix) => from.startsWith(prefix))
  return unsafe ? '/sites' : from
}

export default function LoginPage(): React.ReactElement {
  const location = useLocation()
  const navigate = useNavigate()
  const session = useAuthStore((state) => state.session)
  const login = useAuthStore((state) => state.login)
  const register = useAuthStore((state) => state.register)

  const [mode, setMode] = useState<Mode>('signin')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const toggled = useRef(false)

  const registering = mode === 'register'
  const mismatch = registering && confirm.length > 0 && confirm !== password

  // Only after an explicit toggle — on first paint `autoFocus` owns the caret.
  useEffect(() => {
    if (!toggled.current) return
    const target = mode === 'register' ? nameRef.current : emailRef.current
    target?.focus()
  }, [mode])

  if (session?.needsBootstrap) return <Navigate to="/setup" replace />

  const switchMode = (): void => {
    toggled.current = true
    setMode(registering ? 'signin' : 'register')
    setFormError(null)
    setFieldErrors({})
    setPassword('')
    setConfirm('')
  }

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (registering && !name.trim()) errors.name = 'Enter your name.'
    if (!email.trim()) errors.email = 'Enter your email address.'

    if (!password) {
      errors.password = 'Enter your password.'
    } else if (registering && !passwordStrength(password).meetsMinimum) {
      errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`
    }

    if (registering && confirm !== password) errors.confirm = 'Passwords do not match.'

    return errors
  }

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (submitting) return

    setFormError(null)
    const invalid = validate()
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    setSubmitting(true)
    try {
      if (registering) {
        await register(email.trim(), name.trim(), password)
      } else {
        await login(email.trim(), password)
      }
      // Leaves `submitting` set: this component unmounts with the redirect.
      navigate(returnPath(location.state), { replace: true })
    } catch (error) {
      if (isApiError(error)) {
        const details = fieldErrorsFrom(error.details)
        setFieldErrors(details)
        // The body message only helps when nothing bound to a field.
        setFormError(Object.keys(details).length > 0 ? null : error.message)
      } else {
        setFormError('Something went wrong. Please try again.')
      }
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>{registering ? 'Create your account' : 'Sign in'}</CardTitle>
          <CardDescription>
            {registering
              ? 'Registration is open on this instance. Your sites, keys and media stay private to your account.'
              : 'Use the email and password for your Argus account.'}
          </CardDescription>
        </CardHeader>

        <CardBody>
          <form
            noValidate
            className="space-y-4"
            onSubmit={(event) => {
              void submit(event)
            }}
          >
            {formError ? (
              <AuthNotice>
                <p>{formError}</p>
              </AuthNotice>
            ) : null}

            {registering ? (
              <Input
                ref={nameRef}
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                leadingIcon={<User />}
                error={fieldErrors.name}
                disabled={submitting}
                required
              />
            ) : null}

            <Input
              ref={emailRef}
              label="Email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              autoFocus
              leadingIcon={<AtSign />}
              error={fieldErrors.email}
              disabled={submitting}
              required
            />

            <PasswordField
              label="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={registering ? 'new-password' : 'current-password'}
              leadingIcon={<Lock />}
              showStrength={registering}
              error={fieldErrors.password}
              disabled={submitting}
              required
            />

            {registering ? (
              <PasswordField
                label="Confirm password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                leadingIcon={<Lock />}
                error={mismatch ? 'Passwords do not match.' : fieldErrors.confirm}
                disabled={submitting}
                required
              />
            ) : null}

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={submitting}
              iconLeft={registering ? <UserPlus /> : <LogIn />}
            >
              {registering ? 'Create account' : 'Sign in'}
            </Button>

            {!registering ? (
              <p className="text-center text-xs leading-relaxed text-fg-subtle">
                Forgot your password? An administrator can reset it for you.
              </p>
            ) : null}
          </form>
        </CardBody>

        {session?.allowRegistration ? (
          <CardFooter className="justify-center">
            <p className="text-sm text-fg-muted">
              {registering ? 'Already have an account?' : 'Need an account?'}{' '}
              <button
                type="button"
                onClick={switchMode}
                disabled={submitting}
                className="font-semibold text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current disabled:cursor-not-allowed disabled:opacity-60"
              >
                {registering ? 'Sign in instead' : 'Create one'}
              </button>
            </p>
          </CardFooter>
        ) : null}
      </Card>
    </AuthLayout>
  )
}

export { LoginPage }
